/**
 * Builder-friendly help text for each section of the Construction Phase Plan.
 * Plain English, with a real-world example for what to type in.
 */

export interface CppSectionHelp {
  hint: string;       // one-line plain English: what is this for?
  example: string;    // a real-world filled-in example (NOT the standard text)
}

export const CPP_SECTION_HELP: Record<string, CppSectionHelp> = {
  '1. Description of project': {
    hint: 'What you\'re building, where, and roughly when.',
    example: 'New build of 4 detached houses at Plot 1-4 Fairfield Lane, Surrey. Traditional construction. Start date 12 May 2026, anticipated completion 30 Jan 2027 (~38 weeks).',
  },
  '2. Project team & responsibilities': {
    hint: 'Who\'s who. Names, companies and a phone number for each role.',
    example: 'Client: ACME Developments Ltd (Sarah Jones, 07700 900100).\nPrincipal Designer: Smith Architects (Mark Smith, 07700 900200).\nPrincipal Contractor: [Your company] (Director name, 07700 900300).\nSite manager: John Murphy. Site supervisors: Mike Doyle (groundworks), Pete Hill (carpentry).',
  },
  '3. Management of the work': {
    hint: 'Programme, sub-contractor selection, induction, supervision, inspection.',
    example: 'Programme issued separately. Sub-contractors PQQ\'d and reviewed by [Director]. All ops inducted before first shift. Toolbox talks every Monday 07:30. Daily site walk by John Murphy; weekly recorded inspection.',
  },
  '4. Significant hazards & control measures': {
    hint: 'The big risks for this site. Cross-reference RAMS for each task.',
    example: 'Working at height (scaffold + tile work) — RAMS RM-005, RM-008. Excavation at foundations — RAMS RM-003. Plant/pedestrian on tight site — banksman + 5mph limit. Public footpath alongside boundary — hoarding + signage.',
  },
  '5. Site rules': {
    hint: 'The site rules document referenced — usually fine as standard.',
    example: 'See Site Rules document in the library. Highlights: PPE always, no smoking outside designated area, hot works permit required, working hours 07:30-17:00 Mon-Fri.',
  },
  '6. Welfare arrangements': {
    hint: 'How many people, and what\'s on site for them. Tied to Site Welfare Plan.',
    example: '2x portaloos, 1x welfare cabin (microwave, kettle, sink, 6-seater). Drying area for wet weather. Clean weekly by ACME Welfare. Peak workforce expected 18.',
  },
  '7. Fire & emergency': {
    hint: 'Fire alarm, extinguishers, muster point, hot works rules.',
    example: 'Air-horn alarm system. Muster point: north corner of compound, by gate. CO₂ + foam at welfare cabin. Hot works permit issued by John Murphy. Fire watch 60 min after last spark.',
  },
  '8. First aid': {
    hint: 'First-aiders on site, kit location, A&E hospital + how to get there.',
    example: 'First aiders: John Murphy (FAW), Mike Doyle (EFAW). Kits: welfare cabin and trade van. AED at site office (4 min walk). A&E: Royal Surrey, Egerton Road, Guildford GU2 7XX (12 min drive A3).',
  },
  '9. Traffic management': {
    hint: 'Vehicle routes, pedestrian routes, banksman use, deliveries.',
    example: 'One-way in via main gate, out via south gate. 5mph site limit. Pedestrian-only path along east edge to welfare. Banksman for all reverses; deliveries booked 1hr slots Mon-Fri.',
  },
  '10. Existing services': {
    hint: 'Gas, electric, water, drains, comms — locations, isolation, Permit-to-Dig.',
    example: 'Drawings on file ref ESD-2026-04. Gas: capped meter at boundary. Electric: temp 100A supply by main gate. Water: stop-tap at meter. CAT-scan over every dig; Permit-to-Dig issued by John Murphy.',
  },
  '11. Working at height': {
    hint: 'Scaffold provider, MEWP, tower use, harness use, edge protection.',
    example: 'Scaffold by ACME Scaffolding (PASMA carded). Weekly inspection (every Monday). 1x scissor lift on hire from Speedy. Harness for any unguarded fascia / soffit work.',
  },
  '12. Plant': {
    hint: 'Telehandler, excavator, dumper, etc. Operator competence + segregation.',
    example: '1x JCB 540-170 telehandler (operator: Pete Hill, CPCS A77). 1x 8t excavator (sub-contractor, CPCS A58). No-go zones marked, banksman in radio contact.',
  },
  '13. Hazardous substances': {
    hint: 'COSHH register entries. Common ones: cement, fuel, paint, solvents.',
    example: 'See COSHH register. Significant: Portland cement (skin/eye), red diesel (fuel store, locked), MS Polymer sealant. Spill kit at welfare. Disposal via licensed waste contractor.',
  },
  '14. Asbestos': {
    hint: 'New build = no AC expected. Refurb = survey on file + action if uncovered.',
    example: 'New build — no AC anticipated. If uncovered during groundworks, STOP, isolate, contact licensed contractor (ACME Asbestos, 0800 123 4567).',
  },
  '15. Sub-contractor management': {
    hint: 'PQQ process, induction, RAMS review, day-to-day supervision.',
    example: 'All subs PQQ\'d before engagement (see Subcontractor PQQ). Inducted on first shift. RAMS reviewed by John Murphy before work starts on each task.',
  },
  '16. Communication': {
    hint: 'Daily briefings, toolbox talks, escalation route.',
    example: '07:30 daily briefing. Monday toolbox talk (rota). WhatsApp group for ops. Escalation: site manager → director → HSE if statutory.',
  },
  '17. Health & Safety File': {
    hint: 'What you\'ll hand back to client at completion (drawings, certs, manuals).',
    example: 'Principal Contractor will issue H&S File at handover, in line with Principal Designer\'s template. Includes: as-built drawings, plant manuals, M&E commissioning certs, asbestos clearance (if applicable), structural calcs, energy certs.',
  },
};

export function helpFor(heading: string): CppSectionHelp | null {
  // Match on the section name without its number, so help text survives
  // sections being renumbered.
  const strip = (x: string) => x.replace(/^\d+\.\s*/, '').toLowerCase().trim();
  const want = strip(heading);
  for (const [k, v] of Object.entries(CPP_SECTION_HELP)) {
    if (strip(k) === want) return v;
  }
  return null;
}
